/** Mini UI wireframes for Payroll Help topics. */
export default function PayrollHelpUiSnippet({ variant, darkMode = false }) {
  const shell = darkMode ? "border-[#3C3C4C] bg-[#1a1a1a]" : "border-gray-200 bg-gray-50";
  const panel = darkMode ? "bg-[#2C2C2C] border-[#3C3C4C]" : "bg-white border-gray-200";
  const muted = darkMode ? "text-gray-400" : "text-gray-500";
  const text = darkMode ? "text-gray-100" : "text-gray-800";
  const accent =
    "bg-redhalo-mint/40 border-redhalo-red/50 text-redhalo-navy dark:text-redhalo-mint";

  const snippets = {
    "loan-leave-encashment": (
      <div className={`rounded-none border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Actions · Settings → Policies</div>
        <div className="mb-2 flex flex-wrap gap-1">
          <span className={`rounded-none border px-2 py-0.5 text-[8px] ${accent}`}>Loan</span>
          <span className={`rounded-none border px-2 py-0.5 text-[8px] ${accent}`}>
            Leave Encashment
          </span>
        </div>
        <div className={`rounded-none border p-2 text-[9px] ${panel} ${muted}`}>
          Loan policies · Encashment policies · matched eligibility
        </div>
      </div>
    ),
    "payroll-workspace": (
      <div className={`rounded-none border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-semibold uppercase ${muted}`}>Payroll</div>
        <div className={`rounded-none border ${panel}`}>
          {["EMP-01", "EMP-02"].map((c) => (
            <div
              key={c}
              className={`flex justify-between border-b px-2 py-1 text-[9px] last:border-0 ${text}`}
            >
              <span>{c}</span>
              <span className={muted}>⋮ Tax Planning</span>
            </div>
          ))}
        </div>
      </div>
    ),
    "tax-planning": (
      <div className={`rounded-none border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Tax Planning</div>
        <div className="mb-2 flex gap-1">
          <span className={`rounded-none border px-2 py-0.5 text-[8px] ${accent}`}>Old Regime</span>
          <span className={`rounded-none border px-2 py-0.5 text-[8px] ${panel} ${muted}`}>
            New Regime
          </span>
        </div>
        <div className={`rounded-none border p-2 text-[9px] ${panel} ${muted}`}>
          Prev Employer · PAN · TAN · HRA / 80C tabs
        </div>
      </div>
    ),
    "settings-general": (
      <div className={`rounded-none border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>General Settings</div>
        <div className={`space-y-1 rounded-none border p-2 text-[9px] ${panel}`}>
          <div className={muted}>Service Commences On · read-only</div>
          <div className={muted}>Finalise notify roles</div>
        </div>
      </div>
    ),
    "settings-declaration": (
      <div className={`rounded-none border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Declaration</div>
        <div className={`mb-2 space-y-1 rounded-none border p-2 text-[9px] ${panel}`}>
          <div className={text}>Tax Year 2026-27 · Default Regime</div>
        </div>
        <div className="flex flex-wrap gap-1">
          {["Declaration", "Proof Submission", "Workflow"].map((t, i) => (
            <span
              key={t}
              className={`rounded-none border px-1.5 py-0.5 text-[8px] ${
                i === 0 ? accent : `${panel} ${muted}`
              }`}
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    ),
    "request-types": (
      <div className={`rounded-none border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Request Types</div>
        <div className={`rounded-none border px-2 py-1 text-[9px] ${panel}`}>
          Tax Planning → same form as Actions
        </div>
      </div>
    ),
    "run-payroll-declarations-tds": (
      <div className={`rounded-none border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Declarations and TDS</div>
        <div className="mb-2 flex gap-1">
          <span className={`rounded-none border px-2 py-0.5 text-[8px] ${accent}`}>Declarations</span>
          <span className={`rounded-none border px-2 py-0.5 text-[8px] ${panel} ${muted}`}>TDS</span>
        </div>
        <div className={`rounded-none border p-2 text-[9px] ${panel} ${muted}`}>
          Regime · Template/Import · TDS Δ · Eye · Override / Import
        </div>
      </div>
    ),
    "run-payroll-lop-arrears": (
      <div className={`rounded-none border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>LOP &amp; Arrears</div>
        <div className={`mb-2 rounded-none border p-2 text-[9px] ${panel} ${text}`}>
          Breakdown · 2026-06: ₹8,083.33{" "}
          <span className="text-redhalo-red">◉</span>
        </div>
        <div className={`rounded-none border p-2 text-[9px] ${panel} ${muted}`}>
          Months + attendance · Present / Revised / Diff by component
        </div>
      </div>
    ),
    "run-payroll-process": (
      <div className={`rounded-none border p-3 ${shell}`}>
        <div className={`mb-2 flex items-center justify-between gap-2`}>
          <span className={`text-[10px] font-bold ${text}`}>Process Payroll</span>
          <span className={`rounded-none border px-1.5 py-0.5 text-[8px] ${accent}`}>
            Quick Reports ▾
          </span>
        </div>
        <div className={`rounded-none border p-2 text-[9px] ${panel} ${muted}`}>
          Projection · Register · Reconciliation · Journal
        </div>
      </div>
    ),
    "reports-import": (
      <div className={`rounded-none border p-3 ${shell}`}>
        <div className={`text-[9px] ${muted}`}>Reports · Import · Export / Template</div>
      </div>
    ),
  };

  return snippets[variant] || (
    <div className={`rounded-none border p-3 text-[9px] ${shell} ${muted}`}>
      Feature overview
    </div>
  );
}
